import { useState } from "react";
import {
  HashtagIcon,
  ChartBarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import PopupModal from "./PopupModal";
import CTA from "./CTA";
import ChooseUs from "./ChooseUs";
import Header from "./Header";
import AffiliatedCompany from "./AffiliatedCompany";
import Team from "./Team";
import Footer from "./Footer";
import Testimonial from "./Testimonial";
import Faq from "./Faq";
import Affiliate from "./Affiliate";
import BlogLanding from "./BlogLanding";
import { motion } from "framer-motion";
import { useEffect } from "react";
import ReactGA from "react-ga4";
ReactGA.initialize("G-HD90TEG5P3");
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } },
};
const fadeInSide = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 1, ease: "easeOut" } },
};
const fadeInHeader = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.4, ease: "easeOut" } },
};
const fadeInHeader1 = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.6, ease: "easeOut" } },
};
const fadeInHeader2 = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.8, ease: "easeOut" } },
};
const fadeInPictures = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 1.3, ease: "easeOut" } },
};

const features = [
  {
    name: "Administrative Task",
    description:
      "Scheduling and Calendar Management, Data Entry Filing, Travel Arrangements, Meeting Preparation, Communication, Handling Phone Calls, Employee Onboarding, HR Support",
    icon: DocumentTextIcon,
  },
  {
    name: "Sales And Lead Generation",
    description:
      "Prospecting new clients, Conducting product demonstrations, Creating sales presentations Handling customer inquiries Managing sales leads",
    icon: ChartBarIcon,
  },
  {
    name: "Social Media Management",
    description:
      "Schedule daily posts, Create engaging content, Respond to comments and messages, Maintain brand consistency, Research and use relevant hashtags",
    icon: HashtagIcon,
  },
];

export default function HeroSection() {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowModal(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleMouseEnter = () => {
    setShowModal(true);
  };
  return (
    <>
      <div>
        <Header />
      </div>
      <main className="overflow-hidden">
        {showModal && <PopupModal onClose={handleCloseModal} />}
        <div className="relative isolate">
          <svg
            className="absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
                width={200}
                height={200}
                x="50%"
                y={-1}
                patternUnits="userSpaceOnUse"
              >
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <svg x="50%" y={-1} className="overflow-visible fill-gray-50">
              <path
                d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                strokeWidth={0}
              />
            </svg>
            <rect
              width="100%"
              height="100%"
              strokeWidth={0}
              fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)"
            />
          </svg>
          <div
            className="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48"
            aria-hidden="true"
          >
            <div
              className="aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
              style={{
                clipPath:
                  "polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)",
              }}
            />
          </div>

          <div className="overflow-hidden">
            <div className="mx-auto max-w-7xl px-6 pb-32 pt-36 sm:pt-60 lg:px-8 lg:pt-32">
              <div className="mx-auto max-w-2xl gap-x-16 lg:mx-0 lg:flex lg:max-w-none lg:items-center">
                <div className="relative w-full max-w-xl lg:shrink-0 xl:max-w-2xl">
                  <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInHeader}
                  >
                    <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                      Unlock Your Business{' '}
                      <span className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                        Full Potential
                      </span>{' '}
                      With Executive Haven
                    </h1>
                  </motion.section>
                  <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInHeader1}
                  >
                    <p className="mt-8 text-xl leading-relaxed text-gray-600 sm:max-w-md lg:max-w-2xl">
                      Our expert team provides tailored business solutions that
                      empower your executive team to focus on strategic goals,
                      driving your business towards unparalleled success.
                    </p>
                  </motion.section>
                  <div className="mt-10 flex items-center gap-x-6">
                    <motion.section
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeInHeader2}
                    >
                      <a
                        href="https://calendly.com/admin-executivehaven/discovery-call"
                        style={{ backgroundColor: "#3BC52B" }}
                        className="rounded-md px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                      >
                        Book a call
                      </a>
                    </motion.section>
                  </div>
                </div>
                <motion.section
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInPictures}
                  // className="h-screen"
                >
                  <div className="mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0">
                    <div className="ml-auto w-44 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:order-last lg:pt-36 xl:order-none xl:pt-80">
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&h=528&q=80"
                          alt=""
                          className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                      </div>
                    </div>
                    <div className="mr-auto w-44 flex-none space-y-8 sm:mr-0 sm:pt-52 lg:pt-36">
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1485217988980-11786ced9454?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&h=528&q=80"
                          alt=""
                          className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                      </div>
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&crop=focalpoint&fp-x=.4&w=396&h=528&q=80"
                          alt=""
                          className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                      </div>
                    </div>
                    <div className="w-44 flex-none space-y-8 pt-32 sm:pt-0">
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1670272504528-790c24957dda?ixlib=rb-4.0.3&ixid=MnwxMjA3fDF8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&crop=left&w=400&h=528&q=80"
                          alt=""
                          className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                      </div>
                      <div className="relative">
                        <img
                          src="https://images.unsplash.com/photo-1670272505284-8faba1c31f7d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDF8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&h=528&q=80"
                          alt=""
                          className="aspect-[2/3] w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
                      </div>
                    </div>
                  </div>
                </motion.section>
              </div>
            </div>
          </div>
        </div>
        {/* Feature section with grid */}
        <div className="relative bg-gradient-to-br from-gray-50 to-white py-24 sm:py-32">
          <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>
          <div className="relative mx-auto max-w-md px-6 text-center sm:max-w-3xl lg:max-w-7xl lg:px-8">
            <motion.section
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInSide}
            >
              <div className="space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-600">
                  What we offer
                </h2>
                <p className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                  Our <span className="text-primary-500">Services</span>
                </p>
                <p className="mx-auto max-w-2xl text-xl text-gray-600">
                  Comprehensive solutions designed to elevate your business operations and drive sustainable growth.
                </p>
              </div>
            </motion.section>
            <motion.section
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="mt-20">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {features.map((feature, index) => (
                    <motion.div
                      key={feature.name}
                      className="group relative"
                      whileHover={{ y: -8 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <div className="relative overflow-hidden rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 group-hover:shadow-large">
                        {/* Background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                        
                        {/* Icon container */}
                        <div className="relative z-10">
                          <div className="inline-flex">
                            <span
                              className="inline-flex items-center justify-center rounded-md p-3 shadow-lg"
                              style={{ backgroundColor: "#3BC52B" }}
                            >
                              <feature.icon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                              />
                            </span>
                          </div>
                          
                          {/* Content */}
                          <div className="mt-6">
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors duration-300">
                              {feature.name}
                            </h3>
                            <p className="mt-4 text-gray-600 leading-relaxed">
                              {feature.description}
                            </p>
                          </div>
                          
                        </div>
                        
                        {/* Decorative element */}
                        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 opacity-20 transition-transform duration-300 group-hover:scale-125"></div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.section>
          </div>
        </div>
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          // className="h-screen"
        >
          <CTA />
        </motion.section>
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInSide}
          // className="h-screen"
        >
          <ChooseUs />
        </motion.section>
        {/* <AffiliatedCompany /> */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          // className="h-screen"
        >
          <Testimonial />
        </motion.section>
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInSide}
          // className="h-screen"
        >
          <BlogLanding />
        </motion.section>
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInSide}
          // className="h-screen"
        >
          <Faq />
        </motion.section>
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          // className="h-screen"
        >
          <Team />
        </motion.section>
        <Affiliate />
        <Footer />
      </main>
    </>
  );
}
